/**
 * SyncBadge.tsx
 * @version 2.2.0 — Badge cliquable dans tous les états (sauf "Synchronisé")
 */

import { useEffect, useState, useCallback } from 'react'
import { CheckCircle2, WifiOff, RefreshCw, Clock, Camera, AlertTriangle } from 'lucide-react'
import { useAppStore }       from '../../store/useAppStore'
import { edlDraftStore }     from '../../store/useEdlDraftStore'
import { offlinePhotoStore } from '../../store/offlinePhotoStore'

const POLL_MS = 30_000

export default function SyncBadge() {
  const uploadQueue = useAppStore(s => s.uploadQueue)
  const setStep     = useAppStore(s => s.setStep)

  const [isOnline,   setIsOnline]   = useState(navigator.onLine)
  const [draftCount, setDraftCount] = useState(0)
  const [photoCount, setPhotoCount] = useState(0)

  const refreshCounts = useCallback(async () => {
    try {
      const [drafts, photos] = await Promise.all([
        edlDraftStore.getAll(),
        offlinePhotoStore.countAllPhotos(),
      ])
      setDraftCount(drafts.length)
      setPhotoCount(photos)
    } catch {
      // Silencieux
    }
  }, [])

  useEffect(() => {
    refreshCounts()

    const onOnline  = () => { setIsOnline(true);  refreshCounts() }
    const onOffline = () => { setIsOnline(false); refreshCounts() }
    const onFocus   = () => refreshCounts()

    window.addEventListener('online',  onOnline)
    window.addEventListener('offline', onOffline)
    window.addEventListener('focus',   onFocus)

    const timer = setInterval(refreshCounts, POLL_MS)

    return () => {
      window.removeEventListener('online',  onOnline)
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('focus',   onFocus)
      clearInterval(timer)
    }
  }, [refreshCounts])

  const queueCount  = uploadQueue.length
  const hasFailed   = uploadQueue.some((i: any) => i.status === 'failed')
  const hasAnything = queueCount > 0 || draftCount > 0 || photoCount > 0

  // ── Wrapper cliquable si quelque chose à gérer ──────────────────────────
  const handleClick = () => setStep('queue')

  // ── 1. Échec critique (Badge rouge) ──────────────────────────────────────
  if (hasFailed) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 bg-red-100 text-red-700 text-xs font-bold px-2.5 py-1 rounded-full hover:bg-red-200 transition-colors animate-pulse"
        title="Erreur de synchronisation — Action requise"
      >
        <AlertTriangle size={13} />
        <span>Erreur Sync</span>
      </button>
    )
  }

  // ── 2. Hors ligne (Détection potentiellement imprécise sur mobile) ────────
  if (!isOnline) {
    const colorClass = queueCount > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'
    return (
      <button
        onClick={handleClick}
        className={`flex items-center gap-1.5 ${colorClass} text-xs font-bold px-2.5 py-1 rounded-full transition-colors`}
        title="Voir la file (Détection réseau imprécise)"
      >
        <WifiOff size={13} />
        <span>{queueCount > 0 ? `${queueCount} en attente` : 'Hors ligne'}</span>
      </button>
    )
  }

  // ── 2. Sync en cours (uploadQueue active) ────────────────────────────────
  if (queueCount > 0) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 bg-blue-100 text-blue-700 text-xs font-bold px-2.5 py-1 rounded-full animate-pulse cursor-pointer hover:bg-blue-200 transition-colors"
        title="Voir les EDL en cours de sync"
      >
        <RefreshCw size={13} className="animate-spin" />
        <span>{queueCount} EDL en sync…</span>
      </button>
    )
  }

  // ── 3. Brouillons en attente ─────────────────────────────────────────────
  if (draftCount > 0) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 bg-orange-100 text-orange-700 text-xs font-bold px-2.5 py-1 rounded-full hover:bg-orange-200 transition-colors"
        title="Voir les brouillons en attente"
      >
        <Clock size={13} />
        <span>{draftCount} brouillon{draftCount > 1 ? 's' : ''}</span>
      </button>
    )
  }

  // ── 4. Photos en attente ─────────────────────────────────────────────────
  if (photoCount > 0) {
    return (
      <button
        onClick={handleClick}
        className="flex items-center gap-1.5 bg-orange-100 text-orange-700 text-xs font-bold px-2.5 py-1 rounded-full hover:bg-orange-200 transition-colors"
        title="Voir les photos en attente"
      >
        <Camera size={13} />
        <span>{photoCount} photo{photoCount > 1 ? 's' : ''}</span>
      </button>
    )
  }

  // ── 5. Tout synchronisé — NON cliquable (rien à gérer) ──────────────────
  return (
    <div className="flex items-center gap-1.5 text-green-400 text-xs font-semibold">
      <CheckCircle2 size={14} />
      <span>Synchronisé</span>
    </div>
  )
}